import {
  DialogContent,
  DialogFooter,
  DialogTitle,
  Typography,
  Separator,
  Button,
} from '@ui/components';
import { useVehicleMassImportStore } from '@pages/vehicles/store';
import { useTranslation } from 'react-i18next';
import { Car } from '@ui/components/icons';
import { FC } from 'react';

export const VehicleMassImportSummary: FC<{
  downloadErrorFileCsv: () => void;
  saveImport: () => void;
}> = ({ downloadErrorFileCsv, saveImport }) => {
  const { t } = useTranslation();
  const { isDownloadErrorFileLoading, isSaveImportLoading, summary, removeImport } =
    useVehicleMassImportStore();

  if (!summary) return null;

  return (
    <DialogContent>
      <div className="p-4">
        <DialogTitle>
          <Typography variant="h400">{t('VEHICLES.MASS_IMPORT.SUMMARY.TITLE')}</Typography>
        </DialogTitle>
      </div>

      <section className="p-4 space-y-8">
        <div>
          <Typography variant="h200">{t('VEHICLES.MASS_IMPORT.SUMMARY.DESCRIPTION')}</Typography>

          <div className="border border-border-default rounded-lg mt-6 p-4 space-y-4">
            <div className="flex gap-2 items-center">
              <Car className="text-disabled size-5" />
              <Typography variant="h100">{t('VEHICLES.MASS_IMPORT.SUMMARY.VEHICLES')}</Typography>
            </div>

            <div className="grid grid-cols-3 px-4">
              <Typography variant="paragraphBasicBook">
                {summary.numOfEntities}{' '}
                {t('VEHICLES.MASS_IMPORT.SUMMARY.ELEMENTS_INTERVAL', {
                  postProcess: 'interval',
                  count: summary.numOfEntities,
                })}
              </Typography>

              <Typography variant="paragraphBasicBook">
                {summary.numOfErrors}{' '}
                {t('VEHICLES.MASS_IMPORT.SUMMARY.ERRORS_INTERVAL', {
                  postProcess: 'interval',
                  count: summary.numOfErrors,
                })}
              </Typography>
            </div>
          </div>
          <Button
            disabled={isDownloadErrorFileLoading || isSaveImportLoading}
            className="block ml-auto mt-2"
            onClick={removeImport}
            variant="link"
          >
            {t('VEHICLES.MASS_IMPORT.SUMMARY.REMOVE_IMPORT_AND_RETRY')}
          </Button>
        </div>

        {!!summary.numOfErrors && (
          <>
            <div className="space-y-6">
              <Typography variant="h200">
                {t('VEHICLES.MASS_IMPORT.SUMMARY.MANAGE_ERRORS')}
              </Typography>

              <Button
                disabled={isDownloadErrorFileLoading || isSaveImportLoading}
                isLoading={isDownloadErrorFileLoading}
                onClick={() => downloadErrorFileCsv()}
                variant="secondary"
                wide
              >
                {t('VEHICLES.MASS_IMPORT.SUMMARY.DOWNLOAD_ERRORS_REPORT')}
              </Button>
            </div>
          </>
        )}

        <Separator />
      </section>

      <DialogFooter>
        <Button
          disabled={isSaveImportLoading || isDownloadErrorFileLoading}
          isLoading={isSaveImportLoading}
          onClick={() => saveImport()}
          variant="primary"
          size="large"
          wide
        >
          {t('VEHICLES.MASS_IMPORT.SUMMARY.SAVE_AND_CONTINUE')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};
